import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { filterPhone } from '../utils/inputValidation';
import { getPlaceholders } from '../utils/placeholders';
import { findTarif, formatPrix, currencySymbol } from '../utils/tarifs';
import {
  Calendar as CalendarIcon, Clock, User,
  CheckCircle2, XCircle, Plus, X, Phone, Save,
  AlertCircle, Search, CalendarDays, Filter,
  ChevronRight, ChevronLeft, Users, Pencil,
  LayoutList, CalendarRange
} from 'lucide-react';
import { useToast } from './Toast';
import BookingEditModal from './BookingEditModal';
import TeamCalendar from './TeamCalendar';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Handles both "09h00" (employees) and "09:00" (salon horaires from <input type="time">)
const timeToMinutes = (t) => {
  if (!t) return 0;
  const sep = t.includes('h') ? 'h' : ':';
  const [h, m] = t.split(sep).map(Number);
  return h * 60 + (m || 0);
};

const minutesToTime = (totalMin) => {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
};

const SLOT_INTERVAL = 30;
const DAY_START_MIN = 7 * 60;   // 07h00
const DAY_END_MIN   = 21 * 60;  // 21h00

const ALL_SLOTS = (() => {
  const slots = [];
  for (let m = DAY_START_MIN; m < DAY_END_MIN; m += SLOT_INTERVAL) {
    slots.push(minutesToTime(m));
  }
  return slots;
})();

const DURATIONS = [
  { label: '15 min', value: 15  },
  { label: '30 min', value: 30  },
  { label: '45 min', value: 45  },
  { label: '1 h',    value: 60  },
  { label: '1h30',   value: 90  },
  { label: '2 h',    value: 120 },
  { label: '2h30',   value: 150 },
  { label: '3 h',    value: 180 },
  { label: '3h30',   value: 210 },
  { label: '4 h',    value: 240 },
  { label: '4h30',   value: 270 },
  { label: '5 h',    value: 300 },
  { label: '5h30',   value: 330 },
  { label: '6 h',    value: 360 },
];

/** Retourne les horaires de travail d'un employé pour un jour de semaine donné (0=dim, 1=lun…). */
const getEmployeeHoraire = (emp, dayOfWeek) => {
  if (!emp.horaires || emp.horaires.length === 0) return null;
  // Number() guard: h.jour peut être string "2" ou number 2 selon la source des données
  return emp.horaires.find(h => Number(h.jour) === dayOfWeek) || null;
};

/**
 * Retourne la liste des employés disponibles pour un créneau donné.
 * Un employé est disponible si :
 *   1. Il travaille ce jour (horaires configurés)
 *   2. Le créneau + durée tient dans ses horaires
 *   3. Pas de RDV qui chevauche ce créneau
 */
const getAvailableEmployees = (slotTime, duration, dateStr, employees, bookings, leaves = []) => {
  if (!slotTime || !dateStr) return [];
  const dayOfWeek = new Date(dateStr + 'T12:00:00').getDay();
  const slotStart = timeToMinutes(slotTime);
  const slotEnd   = slotStart + duration;

  return employees.filter(emp => {
    // Exclure les employés en congé pour cette date
    const isOnLeave = leaves.some(l =>
      String(l.employeeId?._id || l.employeeId) === String(emp._id) &&
      dateStr >= new Date(l.dateDebut).toISOString().split('T')[0] &&
      dateStr <= new Date(l.dateFin).toISOString().split('T')[0]
    );
    if (isOnLeave) return false;

    const h = getEmployeeHoraire(emp, dayOfWeek);
    if (!h) return false;
    if (slotStart < timeToMinutes(h.debut)) return false;
    if (slotEnd   > timeToMinutes(h.fin))   return false;

    const empId = emp._id;
    // Seuls les RDV 'Confirmé' bloquent un créneau — Terminé et Annulé libèrent la plage
    const empBookings = bookings.filter(b => {
      const bEmpId = b.employeeId?._id || b.employeeId;
      const bDate  = new Date(b.date).toISOString().split('T')[0];
      return String(bEmpId) === String(empId) && bDate === dateStr && b.status === 'Confirmé';
    });

    return !empBookings.some(b =>
      slotStart < timeToMinutes(b.heureFin) &&
      slotEnd   > timeToMinutes(b.heureDebut)
    );
  });
};

// ─── Statuts visuels ──────────────────────────────────────────────────────────
const STATUS_UI = {
  'Confirmé': { accent: '#2563EB', bg: '#EFF6FF', badge: 'bg-blue-50 text-blue-600 border-blue-100' },
  'Terminé':  { accent: '#059669', bg: '#ECFDF5', badge: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  'Annulé':   { accent: '#DC2626', bg: '#FEF2F2', badge: 'bg-red-50 text-red-500 border-red-100' },
};

// ─── Composant ────────────────────────────────────────────────────────────────

const AdminPlanning = ({ salonId, employees, onFinishBooking, onAssignBooking, salonDevise }) => {
  const showToast = useToast();

  // ── État liste ──
  const [leaves, setLeaves]             = useState([]);
  const [bookings, setBookings]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');
  const [searchTerm, setSearchTerm]     = useState('');

  // ── Vue active ──
  const [activeView, setActiveView]           = useState(
    () => localStorage.getItem('planningView') || 'list'
  );
  const [calendarDate, setCalendarDate]       = useState(
    () => localStorage.getItem('planningCalendarDate') || new Date().toISOString().split('T')[0]
  );

  // ── État modal édition ──
  const [editingBooking, setEditingBooking]   = useState(null);

  // ── État modal ──
  const [showAddModal, setShowAddModal]       = useState(false);
  const [step, setStep]                       = useState(1);
  const [selectedDate, setSelectedDate]       = useState(new Date().toISOString().split('T')[0]);
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [selectedSlot, setSelectedSlot]       = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [clientNom, setClientNom]             = useState('');
  const [clientPrenom, setClientPrenom]       = useState('');
  const [clientTelephone, setClientTelephone] = useState('');
  const [clientMode, setClientMode]           = useState('existing');
  const [clientSearch, setClientSearch]       = useState('');
  const [selectedClient, setSelectedClient]   = useState(null);
  const [clients, setClients]                 = useState([]);
  const [service, setService]                 = useState('');
  const [servicesAjoutes, setServicesAjoutes] = useState([]);
  const [serviceAutre, setServiceAutre]       = useState('');
  const [categories, setCategories]           = useState([]);
  const [montant, setMontant]                 = useState('');
  const [submitting, setSubmitting]           = useState(false);
  const [errorMsg, setErrorMsg]               = useState('');

  // ── Chargement des RDV ──
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/bookings/${salonId}`);
      const sorted = res.data.data.sort((a, b) => {
        const dateA = new Date(a.date), dateB = new Date(b.date);
        if (dateA - dateB !== 0) return dateB - dateA;
        return b.heureDebut.localeCompare(a.heureDebut);
      });
      setBookings(sorted);
    } catch (err) {
      console.error('Erreur chargement bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!salonId) return;
    fetchBookings();
    api.get('/admin/leaves').then(r => setLeaves(r.data.data || [])).catch(() => {});
    api.get('/admin/categories').then(r => setCategories(r.data.data || [])).catch(() => {});
    api.get('/clients').then(r => setClients(r.data.data || [])).catch(() => {});
  }, [salonId]);

  useEffect(() => { localStorage.setItem('planningView', activeView); }, [activeView]);
  useEffect(() => { localStorage.setItem('planningCalendarDate', calendarDate); }, [calendarDate]);

  // ── Helpers modal ──
  const resetModal = () => {
    setStep(1);
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedDuration(30);
    setSelectedSlot('');
    setSelectedEmployee(null);
    setClientNom('');
    setClientPrenom('');
    setClientTelephone('');
    setClientMode('existing');
    setClientSearch('');
    setSelectedClient(null);
    setService('');
    setServicesAjoutes([]);
    setServiceAutre('');
    setMontant('');
    setErrorMsg('');
    setSubmitting(false);
  };

  // Services ajoutés avec leur prix pour la durée choisie (null si pas de tarif)
  const servicesAvecPrix = useMemo(() => servicesAjoutes.map(sv => {
    const cat = sv.categoryId ? categories.find(c => c._id === sv.categoryId) : null;
    return { ...sv, prix: cat ? findTarif(cat, selectedDuration) : null };
  }), [servicesAjoutes, categories, selectedDuration]);

  const totalServices = useMemo(() => {
    const priced = servicesAvecPrix.filter(sv => sv.prix !== null && sv.prix !== undefined);
    return priced.length ? priced.reduce((sum, sv) => sum + Number(sv.prix), 0) : null;
  }, [servicesAvecPrix]);

  useEffect(() => { setMontant(totalServices !== null ? String(totalServices) : ''); }, [totalServices]);

  const ajouterService = () => {
    let nom = '';
    let categoryId = null;
    if (categories.length > 0 && service !== '__autre__') {
      const cat = categories.find(c => c.nom === service);
      if (!cat) return;
      nom = cat.nom;
      categoryId = cat._id;
    } else {
      nom = (service === '__autre__' ? serviceAutre : service).trim();
      if (!nom) return;
    }
    setServicesAjoutes(prev => [...prev, { key: `${Date.now()}-${prev.length}`, nom, categoryId }]);
    setService('');
    setServiceAutre('');
  };

  const retirerService = (key) => setServicesAjoutes(prev => prev.filter(sv => sv.key !== key));

  const openModal  = () => { resetModal(); setShowAddModal(true); };
  const closeModal = () => { setShowAddModal(false); resetModal(); };

  // ── Dérivés ──

  /** Heure de fin calculée depuis le créneau + durée. */
  const heureFin = useMemo(
    () => selectedSlot ? minutesToTime(timeToMinutes(selectedSlot) + selectedDuration) : '',
    [selectedSlot, selectedDuration]
  );

  /**
   * Créneaux à afficher pour le jour sélectionné :
   * un créneau n'est visible que si au moins 1 employé peut le couvrir
   * en entier selon ses horaires (sans tenir compte des RDV ici — c'est
   * vérifié à la volée dans la grille via getAvailableEmployees).
   */
  const activeSlots = useMemo(() => {
    if (!selectedDate) return [];
    const dayOfWeek = new Date(selectedDate + 'T12:00:00').getDay();

    // Pre-compute each employee's [start, end] in minutes once per day change
    const empRanges = employees
      .map(emp => getEmployeeHoraire(emp, dayOfWeek))
      .filter(Boolean)
      .map(h => ({ start: timeToMinutes(h.debut), end: timeToMinutes(h.fin) }));

    return ALL_SLOTS.filter(slotTime => {
      const slotStart = timeToMinutes(slotTime);
      const slotEnd   = slotStart + selectedDuration;
      return empRanges.some(r => slotStart >= r.start && slotEnd <= r.end);
    });
  }, [selectedDate, selectedDuration, employees]);

  /** Employés disponibles pour le créneau sélectionné. */
  const availableForSlot = useMemo(
    () => getAvailableEmployees(selectedSlot, selectedDuration, selectedDate, employees, bookings, leaves),
    [selectedSlot, selectedDuration, selectedDate, employees, bookings, leaves]
  );

  // ── Actions ──

  const handleSubmit = async () => {
    const nomFinal = selectedClient
      ? [selectedClient.prenom, selectedClient.nom].filter(Boolean).join(' ')
      : [clientPrenom.trim(), clientNom.trim()].filter(Boolean).join(' ');
    if (!nomFinal) { setErrorMsg('Le nom du client est requis.'); return; }
    setSubmitting(true);
    setErrorMsg('');
    try {
      // Saisie libre non validée par "Ajouter" : on l'inclut quand même
      const pendingLibre = (categories.length === 0 || service === '__autre__')
        ? (service === '__autre__' ? serviceAutre : service).trim() : '';
      const pendingCat = (categories.length > 0 && service && service !== '__autre__')
        ? categories.find(c => c.nom === service) : null;
      const pendingCatAjoutee = pendingCat && servicesAjoutes.some(sv => sv.categoryId === pendingCat._id);
      const pendingCatPrix = pendingCat && !pendingCatAjoutee ? findTarif(pendingCat, selectedDuration) : null;
      const nomsFinaux = servicesAjoutes.map(sv => sv.nom);
      if (pendingCat && !pendingCatAjoutee) nomsFinaux.push(pendingCat.nom);
      if (pendingLibre) nomsFinaux.push(pendingLibre);
      const serviceValue = nomsFinaux.join(', ');
      const firstCategoryId = servicesAjoutes.find(sv => sv.categoryId)?.categoryId
        || (pendingCat && !pendingCatAjoutee ? pendingCat._id : undefined);

      // Montant : si l'admin n'a pas modifié le pré-remplissage, on y ajoute le prix de la catégorie en attente
      let montantFinal = montant;
      const autoMontant = totalServices !== null ? String(totalServices) : '';
      if (pendingCatPrix !== null && montant === autoMontant) {
        montantFinal = String((totalServices || 0) + pendingCatPrix);
      }
      let prixEnvoye = null;
      if (montantFinal !== '' && Number.isFinite(Number(montantFinal)) && Number(montantFinal) >= 0) {
        prixEnvoye = salonDevise === 'XOF' ? Math.round(Number(montantFinal)) : Number(montantFinal);
      }

      let clientId = selectedClient?._id || undefined;

      // Nouveau client : l'enregistrer dans la base CRM avant le booking
      if (clientMode === 'new' && clientNom.trim()) {
        try {
          const cRes = await api.post('/clients', {
            nom:       clientNom.trim(),
            prenom:    clientPrenom.trim() || '',
            telephone: clientTelephone.trim() || '',
          });
          clientId = cRes.data.data?._id;
          // Rafraîchir la liste locale pour les prochaines recherches
          setClients(prev => [...prev, { ...cRes.data.data, status: 'non_suivi' }]);
        } catch { /* ne pas bloquer le RDV si le CRM échoue */ }
      }

      await api.post('/admin/bookings', {
        salonId,
        employeeId:      selectedEmployee._id,
        clientId,
        clientNom:       nomFinal,
        clientTelephone: selectedClient ? (selectedClient.telephone || '') : clientTelephone.trim(),
        date:            selectedDate,
        heureDebut:      selectedSlot,
        heureFin,
        service:         serviceValue,
        ...(firstCategoryId ? { serviceCategory: firstCategoryId } : {}),
        ...(prixEnvoye !== null ? { prix: prixEnvoye } : {}),
      });
      showToast(
        clientMode === 'new' && clientNom.trim()
          ? 'Rendez-vous créé et client ajouté au CRM'
          : 'Rendez-vous créé avec succès',
        'success'
      );
      fetchBookings();
      closeModal();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      const bookingToUpdate = bookings.find(b => b._id === bookingId);
      await api.put(`/admin/bookings/${bookingId}`, { status: newStatus });
      // Mettre à jour le state local immédiatement (évite l'état périmé)
      setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: newStatus } : b));
      if (newStatus === 'Terminé' && onFinishBooking) {
        onFinishBooking({
          employeeId: bookingToUpdate.employeeId?._id || bookingToUpdate.employeeId,
          clientNom:  bookingToUpdate.clientNom,
          montant:    bookingToUpdate.prix || 0,
          service:    bookingToUpdate.service || 'Prestation',
          bookingId:  bookingId,
        });
      }
    } catch {
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  // Clic sur un créneau + durée choisie dans le calendrier → ouvre le modal à l'étape 3
  const handleSlotClick = (emp, dateStr, slotTime, duration = 30) => {
    resetModal();
    setSelectedDate(dateStr);
    setSelectedSlot(slotTime);
    setSelectedEmployee(emp);
    setSelectedDuration(duration);
    setStep(3);
    setShowAddModal(true);
  };

  const setTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today); setEndDate(today);
  };

  const filteredBookings = bookings.filter(b => {
    const matchEmp    = filterEmployee === 'all' || b.employeeId?._id === filterEmployee;
    const matchStat   = filterStatus   === 'all' || b.status === filterStatus;
    const matchSearch = b.clientNom.toLowerCase().includes(searchTerm.toLowerCase());
    const dateStr     = new Date(b.date).toISOString().split('T')[0];
    let matchDate = true;
    if (startDate && endDate) matchDate = dateStr >= startDate && dateStr <= endDate;
    else if (startDate) matchDate = dateStr >= startDate;
    else if (endDate)   matchDate = dateStr <= endDate;
    return matchEmp && matchStat && matchSearch && matchDate;
  });

  // ── Couleur d'un créneau dans la grille ──
  const slotClassName = (available, isSelected) => {
    if (isSelected)            return 'bg-blue-600 text-white border-blue-600 shadow-lg scale-105';
    if (available.length === 0) return 'bg-slate-50 text-slate-200 border-slate-100 cursor-not-allowed';
    if (available.length === 1) return 'bg-amber-50 text-amber-700 border-amber-100 hover:border-amber-300 hover:shadow-sm';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:border-emerald-300 hover:shadow-sm';
  };

  const slotBadgeClassName = (available, isSelected) => {
    if (isSelected)             return 'text-blue-100';
    if (available.length === 0) return 'text-slate-200';
    if (available.length === 1) return 'text-amber-400';
    return 'text-emerald-400';
  };

  // ─────────────────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 space-y-4">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-400 font-bold">Mise à jour du planning...</p>
    </div>
  );

  return (
    <>
    <div className="space-y-4 pb-6">

      {/* ── TOGGLE LISTE / CALENDRIER ──────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex bg-slate-100 p-1 rounded-2xl gap-1">
          <button
            onClick={() => setActiveView('list')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition-all ${
              activeView === 'list'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <LayoutList size={14} /> Liste
          </button>
          <button
            onClick={() => setActiveView('calendar')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition-all ${
              activeView === 'calendar'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <CalendarRange size={14} /> Calendrier
          </button>
        </div>

        <button onClick={openModal}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-2xl font-black text-xs shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 whitespace-nowrap shrink-0">
          <Plus size={15} /> Nouveau RDV
        </button>
      </div>

      {/* ── VUE CALENDRIER ──────────────────────────────────── */}
      {activeView === 'calendar' && (
        <TeamCalendar
          employees={employees}
          bookings={bookings}
          leaves={leaves}
          date={calendarDate}
          onDateChange={setCalendarDate}
          onSlotClick={handleSlotClick}
          onUpdateStatus={handleUpdateStatus}
          onEditBooking={setEditingBooking}
        />
      )}

      {/* ── VUE LISTE ────────────────────────────────────────── */}
      {activeView === 'list' && (() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const todayBookings = bookings.filter(b => new Date(b.date).toISOString().split('T')[0] === todayStr);
        const todayConfirmed = todayBookings.filter(b => b.status === 'Confirmé').length;
        const todayDone      = todayBookings.filter(b => b.status === 'Terminé').length;
        const todayTotal     = todayBookings.length;

        // Groupement par date
        const grouped = {};
        filteredBookings.forEach(b => {
          const dk = new Date(b.date).toISOString().split('T')[0];
          if (!grouped[dk]) grouped[dk] = [];
          grouped[dk].push(b);
        });
        const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
        const tomorrowStr = new Date(Date.now() + 86400000).toISOString().split('T')[0];

        return (
          <>
          {/* ── Stats aujourd'hui ── */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Aujourd\'hui', value: todayTotal,     accent: '#64748B', bg: '#F8FAFC' },
              { label: 'Confirmés',    value: todayConfirmed, accent: '#2563EB', bg: '#EFF6FF' },
              { label: 'Terminés',     value: todayDone,      accent: '#059669', bg: '#ECFDF5' },
            ].map(s => (
              <div key={s.label} className="rounded-[1.5rem] p-3 md:p-4 border"
                style={{ background: s.bg, borderColor: s.accent + '22' }}>
                <p className="font-black text-2xl md:text-3xl leading-none" style={{ color: s.accent }}>{s.value}</p>
                <p className="text-[10px] font-bold mt-1 uppercase tracking-widest" style={{ color: s.accent + 'AA' }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* ── Recherche ── */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
            <input
              type="text"
              placeholder="Rechercher un client..."
              className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-100 rounded-2xl shadow-sm focus:ring-2 ring-blue-500/20 outline-none font-medium text-sm"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-300 hover:text-slate-500 transition-colors">
                <X size={14} />
              </button>
            )}
          </div>

          {/* ── Filtres unifiés ── */}
          <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
            {/* Période */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-50">
              <button onClick={setTodayFilter}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-100 transition-all shrink-0">
                <CalendarDays size={12} /> Auj.
              </button>
              <div className="flex-1 flex items-center gap-1.5 min-w-0 text-xs">
                <span className="text-slate-300 font-bold shrink-0">Du</span>
                <input type="date" className="bg-transparent border-none outline-none font-bold text-slate-600 text-xs w-full min-w-0"
                  value={startDate} onChange={e => setStartDate(e.target.value)} />
                <span className="text-slate-300 font-bold shrink-0">au</span>
                <input type="date" className="bg-transparent border-none outline-none font-bold text-slate-600 text-xs w-full min-w-0"
                  value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              {(startDate || endDate) && (
                <button onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="shrink-0 p-1.5 text-red-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Employés */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-50 overflow-x-auto no-scrollbar">
              <button onClick={() => setFilterEmployee('all')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition-all shrink-0 ${filterEmployee === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                <Users size={12} /> Tous
              </button>
              {employees.map(emp => {
                const initials = `${emp.prenom?.[0] || ''}${emp.nom?.[0] || ''}`.toUpperCase();
                const isActive = filterEmployee === emp._id;
                return (
                  <button key={emp._id} onClick={() => setFilterEmployee(emp._id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl font-bold text-xs transition-all shrink-0 ${isActive ? 'bg-blue-600 text-white shadow-sm shadow-blue-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center font-black text-[9px] ${isActive ? 'bg-white/20' : 'bg-slate-300 text-white'}`}>
                      {initials}
                    </span>
                    {emp.prenom}
                  </button>
                );
              })}
            </div>

            {/* Statuts */}
            <div className="flex items-center gap-1.5 px-4 py-3 overflow-x-auto no-scrollbar">
              {['all', 'Confirmé', 'Terminé', 'Annulé'].map(stat => {
                const ui = stat !== 'all' ? STATUS_UI[stat] : null;
                const isActive = filterStatus === stat;
                const count = stat === 'all'
                  ? filteredBookings.length
                  : filteredBookings.filter(b => b.status === stat).length;
                return (
                  <button key={stat} onClick={() => setFilterStatus(stat)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-[10px] uppercase tracking-widest border-2 transition-all shrink-0"
                    style={isActive && ui ? { background: ui.bg, borderColor: ui.accent, color: ui.accent }
                      : isActive ? { background: '#0F172A', borderColor: '#0F172A', color: '#fff' }
                      : { background: 'transparent', borderColor: 'transparent', color: '#94A3B8' }}>
                    {stat === 'all' ? 'Tous' : stat}
                    <span className="font-black opacity-60">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Liste groupée par date ── */}
          {filteredBookings.length === 0 ? (
            <div className="bg-white py-16 rounded-[2.5rem] border-2 border-dashed border-slate-100 text-center">
              <CalendarIcon className="mx-auto text-slate-100 mb-4" size={48} />
              <p className="text-slate-400 font-bold text-sm">Aucun rendez-vous sur cette période</p>
              <p className="text-slate-300 text-xs mt-1">Essayez de modifier vos filtres</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedKeys.map(dateKey => {
                const dayBookings = grouped[dateKey];
                const date = new Date(dateKey + 'T12:00:00');
                const isToday    = dateKey === todayStr;
                const isTomorrow = dateKey === tomorrowStr;
                const dateLabel  = isToday    ? 'Aujourd\'hui'
                                 : isTomorrow ? 'Demain'
                                 : date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

                return (
                  <div key={dateKey}>
                    {/* Séparateur de date */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-2 shrink-0">
                        {isToday && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        )}
                        <span className={`text-xs font-black uppercase tracking-widest capitalize ${isToday ? 'text-blue-600' : 'text-slate-400'}`}>
                          {dateLabel}
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-slate-100" />
                      <span className="text-[10px] font-bold text-slate-300 shrink-0">{dayBookings.length} RDV</span>
                    </div>

                    {/* Cartes du jour */}
                    <div className="space-y-2">
                      {dayBookings.map(booking => {
                        const ui = STATUS_UI[booking.status] || STATUS_UI['Confirmé'];
                        const empInitials = booking.employeeId
                          ? `${booking.employeeId.prenom?.[0] || ''}${booking.employeeId.nom?.[0] || ''}`.toUpperCase()
                          : '?';

                        return (
                          <div key={booking._id}
                            className="bg-white rounded-[1.75rem] border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden flex">

                            {/* Barre statut gauche */}
                            <div className="w-1.5 shrink-0" style={{ background: ui.accent }} />

                            <div className="flex-1 p-4">
                              {/* Ligne principale */}
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  {/* Heure en haut — donnée principale */}
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-black text-sm" style={{ color: ui.accent }}>
                                      {booking.heureDebut}
                                    </span>
                                    <span className="text-slate-300 text-xs font-bold">→</span>
                                    <span className="text-slate-400 text-xs font-bold">{booking.heureFin}</span>
                                    <span className={`ml-auto px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest border ${ui.badge}`}>
                                      {booking.status}
                                    </span>
                                  </div>
                                  {/* Nom client */}
                                  <p className={`font-black text-base leading-tight ${booking.status === 'Annulé' ? 'text-slate-300 line-through' : 'text-slate-800'}`}>
                                    {[booking.clientPrenom, booking.clientNom].filter(Boolean).join(' ')}
                                  </p>
                                  {/* Service + téléphone */}
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {booking.service && (
                                      <span className="text-[11px] text-slate-400 font-medium">{booking.service}</span>
                                    )}
                                    {booking.clientTelephone && (
                                      <span className="flex items-center gap-1 text-[11px] text-slate-400 font-medium">
                                        <Phone size={10} /> {booking.clientTelephone}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Avatar employé */}
                                <div className="shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center font-black text-xs"
                                  style={{ background: ui.bg, color: ui.accent }}>
                                  {booking.employeeId ? empInitials : <User size={16} />}
                                </div>
                              </div>

                              {/* Actions — RDV Confirmé */}
                              {booking.status === 'Confirmé' && (
                                <div className="mt-3 pt-3 border-t border-slate-50 space-y-2">
                                  {booking.source === 'public' && !booking.employeeId && onAssignBooking && (
                                    <button onClick={() => onAssignBooking(booking)}
                                      className="w-full flex items-center justify-center gap-2 py-2 bg-violet-50 text-violet-700 border border-violet-100 rounded-xl font-bold text-xs hover:bg-violet-100 transition-all active:scale-95">
                                      <Users size={13} /> Assigner un coiffeur
                                    </button>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => handleUpdateStatus(booking._id, 'Terminé')}
                                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-500 text-white rounded-xl font-bold text-xs shadow-sm shadow-emerald-100 hover:bg-emerald-600 transition-all active:scale-95">
                                      <CheckCircle2 size={13} /> Terminer
                                    </button>
                                    <button onClick={() => handleUpdateStatus(booking._id, 'Annulé')}
                                      className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-red-100 text-red-400 rounded-xl font-bold text-xs hover:bg-red-50 transition-all active:scale-95">
                                      <XCircle size={13} /> Annuler
                                    </button>
                                    <button onClick={() => setEditingBooking(booking)}
                                      className="flex items-center justify-center px-3 py-2 border border-slate-200 text-slate-400 rounded-xl font-bold text-xs hover:bg-slate-50 transition-all active:scale-95">
                                      <Pencil size={13} />
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Réactiver annulé */}
                              {booking.status === 'Annulé' && (
                                <div className="mt-3 pt-3 border-t border-slate-50">
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => handleUpdateStatus(booking._id, 'Confirmé')}
                                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-50 text-blue-600 border border-blue-100 rounded-xl font-bold text-xs hover:bg-blue-100 transition-all active:scale-95">
                                      <CheckCircle2 size={13} /> Confirmé
                                    </button>
                                    <button onClick={() => handleUpdateStatus(booking._id, 'Terminé')}
                                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-xl font-bold text-xs hover:bg-emerald-100 transition-all active:scale-95">
                                      <CheckCircle2 size={13} /> Terminé
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════
          MODAL NOUVEAU RDV - Wizard 3 étapes
      ══════════════════════════════════════════════════════════ */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-end sm:items-center justify-center z-[100] p-0 sm:p-4">
          <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-lg shadow-2xl relative max-h-[92vh] flex flex-col">

            {/* ── En-tête ── */}
            <div className="p-6 pb-4 border-b border-slate-100 shrink-0">
              <button onClick={closeModal}
                className="absolute top-5 right-5 text-slate-300 hover:text-slate-600 transition-colors p-1">
                <X size={22} />
              </button>
              <h2 className="text-xl font-black text-slate-900">Nouveau RDV</h2>

              {/* Indicateur d'étapes */}
              <div className="flex items-center gap-2 mt-4">
                {[1, 2, 3].map(s => (
                  <React.Fragment key={s}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                      s < step  ? 'bg-emerald-500 text-white' :
                      s === step ? 'bg-blue-600 text-white shadow-md shadow-blue-200' :
                                   'bg-slate-100 text-slate-300'
                    }`}>
                      {s < step ? <CheckCircle2 size={14} /> : s}
                    </div>
                    {s < 3 && (
                      <div className={`flex-1 h-0.5 rounded-full transition-all duration-300 ${s < step ? 'bg-emerald-400' : 'bg-slate-100'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-slate-400 text-xs font-bold mt-2">
                {step === 1 ? '① Choisir la date et le créneau' :
                 step === 2 ? '② Choisir un coiffeur disponible' :
                              '③ Informations du client'}
              </p>
            </div>

            {/* ── Corps scrollable ── */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* ─────────── ÉTAPE 1 : Date + Durée + Grille ─────────── */}
              {step === 1 && (
                <>
                  {/* Date */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Date *</label>
                    <input type="date"
                      className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-500/20"
                      value={selectedDate}
                      onChange={e => { setSelectedDate(e.target.value); setSelectedSlot(''); }} />
                  </div>

                  {/* Durée de la prestation */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      Durée de la prestation
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {DURATIONS.map(d => (
                        <button key={d.value} type="button"
                          onClick={() => { setSelectedDuration(d.value); setSelectedSlot(''); }}
                          className={`px-4 py-2 rounded-xl font-black text-xs transition-all border ${
                            selectedDuration === d.value
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                              : 'bg-slate-50 text-slate-500 border-slate-100 hover:border-blue-200 hover:text-blue-600'
                          }`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Grille des créneaux */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                      <Clock size={12} /> Créneaux disponibles
                    </label>

                    {/* Légende */}
                    <div className="flex items-center gap-3 text-[10px] font-bold text-slate-400 mb-1">
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-200 inline-block" /> Libre
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-200 inline-block" /> 1 dispo
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-slate-100 border border-slate-200 inline-block" /> Complet
                      </span>
                    </div>

                    {activeSlots.length === 0 ? (
                      <div className="text-center py-10">
                        <CalendarIcon className="mx-auto text-slate-200 mb-2" size={36} />
                        <p className="text-slate-400 text-sm font-bold">Aucun créneau disponible ce jour.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {activeSlots.map(slot => {
                          const available = getAvailableEmployees(slot, selectedDuration, selectedDate, employees, bookings, leaves);
                          const isSelected = slot === selectedSlot;
                          const isFull = available.length === 0;
                          return (
                            <button key={slot} type="button"
                              disabled={isFull}
                              onClick={() => setSelectedSlot(slot)}
                              className={`relative flex flex-col items-center py-3 px-2 rounded-2xl border-2 font-black text-xs transition-all active:scale-95 ${slotClassName(available, isSelected)}`}>
                              <span className="text-sm leading-tight">{slot}</span>
                              <span className={`text-[9px] font-bold mt-0.5 ${slotBadgeClassName(available, isSelected)}`}>
                                {isFull
                                  ? 'Complet'
                                  : `${available.length} dispo`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ─────────── ÉTAPE 2 : Employé ─────────── */}
              {step === 2 && (
                <>
                  {/* Récap créneau */}
                  <div className="flex items-center gap-3 p-3.5 bg-blue-50 rounded-2xl border border-blue-100">
                    <Clock size={16} className="text-blue-500 shrink-0" />
                    <div>
                      <p className="text-blue-800 font-black text-sm capitalize">
                        {new Date(selectedDate + 'T12:00:00').toLocaleDateString('fr-FR', {
                          weekday: 'long', day: 'numeric', month: 'long'
                        })}
                      </p>
                      <p className="text-blue-500 font-bold text-xs mt-0.5">
                        {selectedSlot} → {heureFin} &nbsp;·&nbsp; {DURATIONS.find(d => d.value === selectedDuration)?.label}
                      </p>
                    </div>
                  </div>

                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                    Coiffeurs disponibles ({availableForSlot.length})
                  </label>

                  {availableForSlot.length === 0 ? (
                    <div className="text-center py-10">
                      <Users className="mx-auto text-slate-200 mb-3" size={40} />
                      <p className="text-slate-400 font-bold text-sm">Aucun coiffeur disponible à ce créneau.</p>
                      <button onClick={() => setStep(1)}
                        className="mt-3 text-blue-500 font-bold text-xs hover:underline">
                        ← Choisir un autre créneau
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {availableForSlot.map(emp => {
                        const isSelected = selectedEmployee?._id === emp._id;
                        const initials   = `${emp.prenom?.[0] || ''}${emp.nom?.[0] || ''}`.toUpperCase();

                        // RDV confirmés à venir pour cet employé ce jour
                        const dayBookings = bookings.filter(b => {
                          const bDate  = new Date(b.date).toISOString().split('T')[0];
                          const bEmpId = b.employeeId?._id || b.employeeId;
                          return bEmpId === emp._id && bDate === selectedDate && b.status === 'Confirmé';
                        });

                        return (
                          <button key={emp._id} type="button"
                            onClick={() => setSelectedEmployee(emp)}
                            className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left active:scale-[0.99] ${
                              isSelected
                                ? 'bg-blue-50 border-blue-400 shadow-md'
                                : 'bg-white border-slate-100 hover:border-slate-200 hover:shadow-sm'
                            }`}>
                            {/* Initiales */}
                            <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center font-black text-sm transition-all ${
                              isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {initials}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className={`font-black text-sm ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>
                                {emp.prenom} {emp.nom}
                              </p>
                              <p className="text-slate-400 text-[11px] font-bold mt-0.5">
                                {dayBookings.length === 0
                                  ? 'Aucun RDV confirmé ce jour'
                                  : `${dayBookings.length} RDV confirmé${dayBookings.length > 1 ? 's' : ''} ce jour`}
                              </p>
                            </div>

                            {isSelected && <CheckCircle2 size={20} className="text-blue-500 shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ─────────── ÉTAPE 3 : Infos client ─────────── */}
              {step === 3 && (
                <>
                  {/* Récap complet */}
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2.5">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                      <CalendarIcon size={13} className="text-blue-500 shrink-0" />
                      <span className="capitalize">
                        {new Date(selectedDate + 'T12:00:00').toLocaleDateString('fr-FR', {
                          weekday: 'long', day: 'numeric', month: 'long'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                      <Clock size={13} className="text-blue-500 shrink-0" />
                      {selectedSlot} → {heureFin}
                      <span className="text-slate-400 font-medium">
                        ({DURATIONS.find(d => d.value === selectedDuration)?.label})
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                      <User size={13} className="text-purple-500 shrink-0" />
                      {selectedEmployee?.prenom} {selectedEmployee?.nom}
                    </div>
                  </div>

                  {errorMsg && (
                    <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold flex items-center gap-2 rounded-r-xl">
                      <AlertCircle size={15} className="shrink-0" /> {errorMsg}
                    </div>
                  )}

                  {/* ── Client picker ── */}
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client *</label>

                    {/* Mode toggle */}
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'existing', label: 'Client existant', sub: 'Chercher dans ma base', icon: '🔍' },
                        { key: 'new',      label: 'Nouveau client',   sub: 'Première venue',        icon: '✨' },
                      ].map(m => {
                        const active = clientMode === m.key;
                        return (
                          <button
                            key={m.key}
                            type="button"
                            onClick={() => {
                              setClientMode(m.key);
                              setSelectedClient(null);
                              setClientSearch('');
                              setClientNom('');
                              setClientTelephone('');
                              setErrorMsg('');
                            }}
                            className="flex flex-col items-start gap-0.5 p-3 rounded-2xl border-2 text-left transition-all"
                            style={{
                              borderColor: active ? '#2563eb' : '#e2e8f0',
                              background:  active ? '#eff6ff' : '#f8fafc',
                            }}
                          >
                            <span className="text-base leading-none">{m.icon}</span>
                            <span className="text-xs font-black mt-1 leading-tight" style={{ color: active ? '#2563eb' : '#334155' }}>{m.label}</span>
                            <span className="text-[10px] font-medium" style={{ color: active ? '#93c5fd' : '#94a3b8' }}>{m.sub}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Mode : client existant */}
                    {clientMode === 'existing' && (() => {
                      const searchLower = clientSearch.toLowerCase();
                      const results = clientSearch.length >= 1
                        ? clients.filter(c => {
                            const full = [c.prenom, c.nom, c.telephone, c.email].filter(Boolean).join(' ').toLowerCase();
                            return full.includes(searchLower);
                          }).slice(0, 6)
                        : [];

                      const statusColor = { a_jour: '#10b981', bientot: '#f59e0b', en_retard: '#ef4444', non_suivi: '#cbd5e1' };

                      if (selectedClient) {
                        const initial = ((selectedClient.prenom || selectedClient.nom || '?')[0]).toUpperCase();
                        const fullName = [selectedClient.prenom, selectedClient.nom].filter(Boolean).join(' ');
                        const bg = statusColor[selectedClient.status] || '#cbd5e1';
                        return (
                          <div className="flex items-center gap-3 p-3.5 rounded-2xl border-2 border-blue-300 bg-blue-50">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white flex-shrink-0" style={{ background: bg }}>
                              {initial}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-black text-sm text-slate-800 leading-tight">{fullName}</p>
                              {selectedClient.telephone && (
                                <p className="text-xs text-slate-500 font-medium mt-0.5">{selectedClient.telephone}</p>
                              )}
                              {(selectedClient.totalVisits > 0 || selectedClient.preferredServiceId?.nom) && (
                                <p className="text-[10px] text-blue-400 font-bold mt-0.5">
                                  {selectedClient.totalVisits > 0 && `${selectedClient.totalVisits} visite${selectedClient.totalVisits > 1 ? 's' : ''}`}
                                  {selectedClient.totalVisits > 0 && selectedClient.preferredServiceId?.nom && ' · '}
                                  {selectedClient.preferredServiceId?.nom}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => { setSelectedClient(null); setClientSearch(''); }}
                              className="flex-shrink-0 text-xs font-black text-blue-400 hover:text-blue-600 transition px-2 py-1 rounded-xl hover:bg-blue-100"
                            >
                              Changer
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div className="relative">
                          <div className="relative">
                            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                            <input
                              autoFocus
                              className="w-full pl-9 pr-4 py-3 bg-slate-50 rounded-2xl outline-none text-sm font-medium text-slate-700 border-2 border-transparent focus:border-blue-300 transition-all"
                              placeholder="Rechercher par nom, téléphone…"
                              value={clientSearch}
                              onChange={e => { setClientSearch(e.target.value); setErrorMsg(''); }}
                            />
                          </div>

                          {clientSearch.length >= 1 && (
                            <div className="mt-1.5 rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                              {results.length === 0 ? (
                                <div className="px-4 py-4 text-xs text-slate-400 font-medium text-center">
                                  Aucun client trouvé pour <span className="font-black">"{clientSearch}"</span>
                                  <br />
                                  <button
                                    type="button"
                                    className="mt-2 text-blue-500 font-black hover:underline"
                                    onClick={() => { setClientMode('new'); setClientNom(clientSearch); setClientSearch(''); }}
                                  >
                                    Créer comme nouveau client
                                  </button>
                                </div>
                              ) : (
                                results.map(c => {
                                  const initial = ((c.prenom || c.nom || '?')[0]).toUpperCase();
                                  const fullName = [c.prenom, c.nom].filter(Boolean).join(' ');
                                  const bg = statusColor[c.status] || '#cbd5e1';
                                  return (
                                    <button
                                      key={c._id}
                                      type="button"
                                      onClick={() => { setSelectedClient(c); setClientSearch(''); setErrorMsg(''); }}
                                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-t border-slate-50 first:border-t-0"
                                    >
                                      <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black text-white flex-shrink-0" style={{ background: bg }}>
                                        {initial}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-slate-800 leading-tight truncate">{fullName}</p>
                                        <p className="text-[10px] text-slate-400 font-medium mt-0.5 truncate">
                                          {[
                                            c.telephone,
                                            c.totalVisits > 0 && `${c.totalVisits} visite${c.totalVisits > 1 ? 's' : ''}`,
                                            c.preferredServiceId?.nom,
                                          ].filter(Boolean).join(' · ')}
                                        </p>
                                      </div>
                                      <ChevronRight size={13} className="text-slate-300 flex-shrink-0" />
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          )}

                          {clients.length === 0 && (
                            <p className="text-[10px] text-slate-400 font-medium mt-2 text-center">
                              Aucun client dans votre base · passez en "Nouveau client"
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {/* Mode : nouveau client */}
                    {clientMode === 'new' && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Prénom</p>
                            <input
                              autoFocus
                              className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm border-2 border-transparent focus:border-blue-300 transition-all"
                              placeholder={getPlaceholders(salonDevise).prenom}
                              value={clientPrenom}
                              onChange={e => { setClientPrenom(e.target.value); setErrorMsg(''); }}
                            />
                          </div>
                          <div className="space-y-1">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Nom *</p>
                            <input
                              className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm border-2 border-transparent focus:border-blue-300 transition-all"
                              placeholder={getPlaceholders(salonDevise).nom}
                              value={clientNom}
                              onChange={e => { setClientNom(e.target.value); setErrorMsg(''); }}
                            />
                          </div>
                        </div>
                        <input
                          type="tel"
                          className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-medium text-slate-700 text-sm border-2 border-transparent focus:border-blue-300 transition-all"
                          placeholder={getPlaceholders(salonDevise).telephone}
                          value={clientTelephone}
                          onChange={e => setClientTelephone(filterPhone(e.target.value))}
                        />
                        <div className="flex items-center gap-2 px-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                          <p className="text-[10px] text-slate-400 font-medium">
                            Ce client sera automatiquement ajouté à votre base CRM
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Services */}
                  <div className="space-y-1.5">
                    <label htmlFor="admin-service-select" className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Services <span className="text-slate-300">(optionnel)</span></label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {categories.length > 0 ? (
                        <select
                          id="admin-service-select"
                          className="flex-1 min-w-0 p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-500/20"
                          value={service}
                          onChange={e => { setService(e.target.value); setServiceAutre(''); }}>
                          <option value="">Sélectionner un service</option>
                          {categories.map(c => (
                            <option key={c._id} value={c.nom}>{c.nom}</option>
                          ))}
                          <option value="__autre__">Autre / Saisie libre</option>
                        </select>
                      ) : (
                        <input
                          id="admin-service-select"
                          className="flex-1 min-w-0 p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-500/20"
                          placeholder="Coiffure, Soin, Tresse…"
                          value={service}
                          onChange={e => setService(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); ajouterService(); } }} />
                      )}
                      <button
                        type="button"
                        onClick={ajouterService}
                        disabled={categories.length > 0
                          ? (service === '' || (service === '__autre__' && !serviceAutre.trim()))
                          : !service.trim()}
                        className="px-5 py-3 bg-blue-600 text-white rounded-2xl font-black text-sm hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed">
                        Ajouter
                      </button>
                    </div>
                    {categories.length > 0 && service === '__autre__' && (
                      <input
                        aria-label="Préciser le service"
                        className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-500/20"
                        placeholder="Préciser le service…"
                        value={serviceAutre}
                        onChange={e => setServiceAutre(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); ajouterService(); } }} />
                    )}

                    {servicesAvecPrix.length > 0 && (
                      <ul className="space-y-1.5" aria-label="Services ajoutés">
                        {servicesAvecPrix.map(sv => (
                          <li key={sv.key} className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-50 rounded-2xl">
                            <span className="font-bold text-sm text-slate-700 truncate">{sv.nom}</span>
                            <span className="flex items-center gap-2 shrink-0">
                              {sv.prix !== null && (
                                <span className="text-xs font-black text-slate-500">{formatPrix(sv.prix, salonDevise)}</span>
                              )}
                              <button
                                type="button"
                                aria-label={`Retirer ${sv.nom}`}
                                onClick={() => retirerService(sv.key)}
                                className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-all">
                                <X size={14} />
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {totalServices !== null && (
                      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-2xl">
                        <div>
                          <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Total des services</p>
                          <p className="text-lg font-black text-emerald-700 leading-tight" data-testid="tarif-prix">
                            {formatPrix(totalServices, salonDevise)}
                          </p>
                        </div>
                        <label className="flex items-center gap-1.5">
                          <span className="sr-only">Montant du rendez-vous</span>
                          <input
                            type="number"
                            min="0"
                            aria-label="Montant du rendez-vous"
                            value={montant}
                            onChange={e => setMontant(e.target.value)}
                            className="w-24 p-2 bg-white rounded-xl text-right font-black text-sm text-slate-700 border border-emerald-100 outline-none focus:border-emerald-400"
                          />
                          <span className="text-[10px] font-black text-emerald-600">{currencySymbol(salonDevise)}</span>
                        </label>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ── Pied fixe ── */}
            <div className="p-6 pt-4 border-t border-slate-100 shrink-0 flex gap-3">
              {step > 1 && (
                <button onClick={() => setStep(s => s - 1)}
                  className="flex items-center gap-1.5 px-5 py-3 bg-slate-100 text-slate-600 rounded-2xl font-black text-sm hover:bg-slate-200 transition-all active:scale-95">
                  <ChevronLeft size={16} /> Retour
                </button>
              )}

              {step === 1 && (
                <button
                  disabled={!selectedSlot}
                  onClick={() => setStep(2)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-lg hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed">
                  Choisir un coiffeur <ChevronRight size={16} />
                </button>
              )}

              {step === 2 && (
                <button
                  disabled={!selectedEmployee}
                  onClick={() => setStep(3)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-lg hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed">
                  Infos du client <ChevronRight size={16} />
                </button>
              )}

              {step === 3 && (
                <button
                  disabled={submitting || (clientMode === 'existing' ? !selectedClient : !clientNom.trim() && !clientPrenom.trim())}
                  onClick={handleSubmit}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-2xl font-black text-sm shadow-lg hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enregistrement…
                    </>
                  ) : (
                    <><Save size={16} /> Confirmer le RDV</>
                  )}
                </button>
              )}
            </div>

          </div>
        </div>
      )}
    </div>

    {/* ══════════════════════════════════════════════════════════
        MODAL MODIFICATION RDV
    ══════════════════════════════════════════════════════════ */}
    {editingBooking && (
      <BookingEditModal
        booking={editingBooking}
        employees={employees}
        salonDevise={salonDevise}
        onClose={() => setEditingBooking(null)}
        onSaved={() => {
          setEditingBooking(null);
          fetchBookings();
        }}
      />
    )}

    </>
  );
};

export default AdminPlanning;
